import type {
  QueryParams,
  RecentReportSerialized,
  ReportFilter,
  ReportType,
  SortBy,
} from '@ltvco/refresh-lib/v2';

function baseReportTypesFromQuery(queryParams: QueryParams): ReportType[] {
  return ((queryParams.report_type || []) as ReportFilter[]).filter(
    (type): type is ReportType => type !== 'monitored_report'
  );
}

export const LIST_VIEW_PAGE_SIZE = 20;

export const phoneAndFraudScanFilterOptions = [
  'reverse_phone_report',
  'phone_fraud_report',
  'email_fraud_report',
  'ip_fraud_report',
  'url_fraud_report',
] as const;

function reportSearchHaystack(report: RecentReportSerialized): string {
  return [
    report.reportData,
    report.reportTypeFormatted,
    report.detailsCityState,
    report.id,
    report.firstName,
    report.lastName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matchesSearch(
  report: RecentReportSerialized,
  search: string
): boolean {
  if (!search.trim()) {
    return true;
  }
  return reportSearchHaystack(report).includes(search.trim().toLowerCase());
}

function matchesReportTypes(
  report: RecentReportSerialized,
  effectiveTypes: ReportType[],
  isPhoneOnlyVariation: boolean
): boolean {
  if (effectiveTypes.length === 0) {
    if (isPhoneOnlyVariation) {
      return (phoneAndFraudScanFilterOptions as readonly string[]).includes(
        report.reportType
      );
    }
    return true;
  }
  return effectiveTypes.includes(report.reportType as ReportType);
}

function sortValueForReport(
  report: RecentReportSerialized,
  sortBy: SortBy
): string | number {
  switch (sortBy) {
    case 'created_at':
      return Date.parse(report.createdAt) || 0;
    case 'updated_at':
      return Date.parse(report.updatedAt) || 0;
    case 'last_viewed_at':
      return Date.parse(report.lastViewedAt) || 0;
    case 'alphabetical':
    default:
      return report.reportData?.toLowerCase() || '';
  }
}

export function getFilteredListViewReportCount(
  reports: RecentReportSerialized[],
  queryParams: QueryParams,
  isPhoneOnlyVariation: boolean
): number {
  const baseReportTypes = baseReportTypesFromQuery(queryParams);
  const effectiveReportTypes: ReportType[] =
    isPhoneOnlyVariation && baseReportTypes.length === 0
      ? [...phoneAndFraudScanFilterOptions]
      : baseReportTypes;

  const search = queryParams.search_by || '';

  return reports.filter(
    (r) =>
      matchesSearch(r, search) &&
      matchesReportTypes(r, effectiveReportTypes, isPhoneOnlyVariation)
  ).length;
}

export function filterSortPaginateListViewReports(
  reports: RecentReportSerialized[],
  queryParams: QueryParams,
  isPhoneOnlyVariation: boolean
): RecentReportSerialized[] {
  const baseReportTypes = baseReportTypesFromQuery(queryParams);
  const effectiveReportTypes: ReportType[] =
    isPhoneOnlyVariation && baseReportTypes.length === 0
      ? [...phoneAndFraudScanFilterOptions]
      : baseReportTypes;

  const search = queryParams.search_by || '';

  let result = reports.filter(
    (r) =>
      matchesSearch(r, search) &&
      matchesReportTypes(r, effectiveReportTypes, isPhoneOnlyVariation)
  );

  const sortBy = queryParams.sort_by || 'last_viewed_at';
  const sortDir = queryParams.sort_direction || 'desc';

  const isNumericSort =
    sortBy === 'created_at' ||
    sortBy === 'updated_at' ||
    sortBy === 'last_viewed_at';

  result = [...result].sort((a, b) => {
    const sortKeyA = sortValueForReport(a, sortBy);
    const sortKeyB = sortValueForReport(b, sortBy);
    if (isNumericSort) {
      const comparison = (sortKeyA as number) - (sortKeyB as number);
      return sortDir === 'asc' ? comparison : -comparison;
    }
    const comparison = String(sortKeyA).localeCompare(String(sortKeyB));
    return sortDir === 'asc' ? comparison : -comparison;
  });

  const rawPage = Number(queryParams.page);
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const start = (page - 1) * LIST_VIEW_PAGE_SIZE;
  return result.slice(start, start + LIST_VIEW_PAGE_SIZE);
}
